import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Globe2, Palette, Inbox, LogOut, BarChart3, Paintbrush, MessageCircle, CreditCard, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ImageUpload } from "@/components/ImageUpload";
import { AdminButton, Field, PageTitle, useList } from "@/components/admin/kit";
import { useProduct } from "@/threedvista/product";

const NAV = [["", "Tours", Globe2], ["analytics", "Analytics", BarChart3], ["leads", "Leads", Inbox], ["skin", "Tour skin", Paintbrush], ["assistant", "AI assistant", MessageCircle], ["connect", "Giraffe360 API", KeyRound, "giraffe"], ["branding", "Branding", Palette], ["plan", "Plan", CreditCard]];

export function TdvLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const P = useProduct();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user || user.product !== P.key) return <Navigate to={`${P.base}/login`} replace />;
  return (
    <div className="min-h-screen bg-[#F6F5F2]" data-testid="tdv-app">
      <header className="bg-[#0B1220] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2 sm:gap-6">
          <Link to={`${P.base}/app`} className="font-serif text-lg shrink-0">Showhome <span className="text-[#C9A227] hidden sm:inline">for {P.name}</span></Link>
          <nav className="flex gap-1 overflow-x-auto">
            {NAV.filter((n) => !n[3] || n[3] === P.key).map(([to, label, Icon]) => (
              <NavLink key={label} end={!to} to={`${P.base}/app${to ? `/${to}` : ""}`} className={({ isActive }) => `h-9 px-3 rounded-full text-sm inline-flex items-center gap-2 transition-colors ${isActive ? "bg-white/15" : "hover:bg-white/10 text-white/70"}`} data-testid={`tdv-nav-${to || "tours"}`}>
                <Icon className="w-4 h-4" /><span className="hidden sm:inline">{label}</span>
              </NavLink>
            ))}
          </nav>
          <button onClick={async () => { await logout(); nav(`${P.base}/login`); }} className="ml-auto h-9 px-3 rounded-full text-sm inline-flex items-center gap-2 hover:bg-white/10" data-testid="tdv-logout"><LogOut className="w-4 h-4" /><span className="hidden sm:inline">Log out</span></button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10"><Outlet /></main>
    </div>
  );
}

export function TdvBranding() {
  const [a, setA] = useState(null);
  const P = useProduct();
  useEffect(() => { api.get("/tdv/account").then((r) => setA(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!a) return null;
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });
  const save = async () => {
    try { const { data } = await api.put("/tdv/account", { name: a.name, logo_url: a.logo_url || "", primary_color: a.primary_color, accent_color: a.accent_color, notify_email: a.notify_email || "", phone: a.phone || "" }); setA(data); toast.success("Saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="tdv-branding">
      <PageTitle title="Branding" sub={`${a.plan_name} · ${a.tours} of ${a.tour_limit ?? "unlimited"} tours used`} />
      <div className="rounded-3xl bg-white border p-6 grid md:grid-cols-2 gap-5 max-w-3xl">
        <Field label="Business name"><Input value={a.name} maxLength={120} onChange={set("name")} data-testid="tdv-brand-name" /></Field>
        <Field label="Logo"><ImageUpload value={a.logo_url || ""} onChange={(v) => setA({ ...a, logo_url: v })} testid="tdv-brand-logo" /></Field>
        <Field label="Main colour"><Input type="color" className="h-11 p-1" value={a.primary_color} onChange={set("primary_color")} data-testid="tdv-brand-primary" /></Field>
        <Field label="Accent colour"><Input type="color" className="h-11 p-1" value={a.accent_color} onChange={set("accent_color")} data-testid="tdv-brand-accent" /></Field>
        <Field label="Send lead alerts to"><Input type="email" value={a.notify_email || ""} onChange={set("notify_email")} data-testid="tdv-brand-email" /></Field>
        <Field label="Phone"><Input value={a.phone || ""} maxLength={40} onChange={set("phone")} data-testid="tdv-brand-phone" /></Field>
        <div className="md:col-span-2"><AdminButton onClick={save} data-testid="tdv-brand-save">Save branding</AdminButton></div>
      </div>
      <div className="rounded-3xl bg-[#0B1220] text-white p-6 max-w-3xl mt-6" data-testid="tdv-upgrade-card">
        <p className="font-serif text-2xl">Need more tours?</p>
        <p className="text-sm text-white/70 mt-1">Your {a.plan_name} plan includes {a.tour_limit ?? "unlimited"} {P.unit}{a.tour_limit === 1 ? "" : "s"}. Choose a bigger plan any time.</p>
        <Link to={`${P.base}/app/plan`} className="inline-flex mt-4 h-10 px-5 rounded-full bg-[#C9A227] text-slate-950 text-sm font-semibold items-center" data-testid="tdv-upgrade-contact">See plans and pay</Link>
      </div>
    </div>
  );
}

export function TdvLeads() {
  const [leads] = useList("/tdv/leads");
  const P = useProduct();
  return (
    <div data-testid="tdv-leads">
      <PageTitle title="Leads" sub={`Viewing requests from your ${P.name} tours.`} />
      <div className="rounded-3xl bg-white border divide-y">
        {(leads || []).map((l, i) => (
          <div key={l.id} className="p-5 grid sm:grid-cols-[1fr_auto] gap-2" data-testid={`tdv-lead-${i}`}>
            <div><p className="font-semibold">{l.name} <span className="text-slate-500 font-normal text-sm">{l.email} {l.phone}</span></p>{l.message && <p className="text-sm text-slate-600 mt-1">{l.message}</p>}</div>
            <p className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString()}</p>
          </div>
        ))}
        {leads?.length === 0 && <p className="p-6 text-sm text-slate-500" data-testid="tdv-leads-empty">No leads yet. Share your tour link to start getting viewing requests.</p>}
      </div>
    </div>
  );
}
